import { useEffect, useState } from 'react'
import { Code, Loader2, AlertTriangle } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import auditLogService from '@/services/auditLogService'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'

const ENTITY_LABELS = {
  announcement: 'Anuncio',
  event: 'Evento',
  success_story: 'Caso de éxito',
  manager_profile: 'Perfil',
  kpi: 'KPI',
  faq: 'FAQ',
  team: 'Equipo',
  hub: 'Hub',
}

const ACTION_LABELS = {
  create: 'Creó',
  update: 'Editó',
  delete: 'Eliminó',
  publish: 'Publicó',
  unpublish: 'Despublicó',
  archive: 'Archivó',
}

const ACTION_VARIANTS = {
  create: 'success',
  update: 'default',
  delete: 'danger',
  publish: 'success',
  unpublish: 'warning',
  archive: 'secondary',
}

function formatDateTime(d) {
  if (!d) return ''
  return new Date(d).toLocaleString('es', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function AuditLog() {
  const { partner } = useAuthStore()
  const [logs, setLogs] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [filters, setFilters] = useState({ entityType: '', action: '' })
  const [diffOpen, setDiffOpen] = useState(false)
  const [activeLog, setActiveLog] = useState(null)

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters])

  async function load() {
    try {
      setIsLoading(true)
      const res = await auditLogService.list({ ...filters, limit: 200 })
      setLogs(res.data || [])
    } finally {
      setIsLoading(false)
    }
  }

  if (partner?.organizationType !== 'country_manager') {
    return (
      <PageContainer>
        <Alert variant="warning">
          <AlertTriangle size={16} />
          <AlertTitle>Acceso restringido</AlertTitle>
          <AlertDescription>
            Solo los Country Managers pueden ver el historial.
          </AlertDescription>
        </Alert>
      </PageContainer>
    )
  }

  const showDiff = (log) => {
    setActiveLog(log)
    setDiffOpen(true)
  }

  return (
    <PageContainer>
      <PageHeader
        title="Historial de actividad"
        description="Registro inmutable de las acciones del Mini-CMS de tu país."
      />

      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-3 sm:grid-cols-2 sm:max-w-md">
            <Select
              value={filters.entityType || 'all'}
              onValueChange={(v) =>
                setFilters({ ...filters, entityType: v === 'all' ? '' : v })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Entidad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las entidades</SelectItem>
                {Object.entries(ENTITY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.action || 'all'}
              onValueChange={(v) =>
                setFilters({ ...filters, action: v === 'all' ? '' : v })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Acción" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las acciones</SelectItem>
                {Object.entries(ACTION_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>
                    {v}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Card>
          <CardContent className="flex justify-center py-12">
            <Loader2 size={28} className="animate-spin text-primary" />
          </CardContent>
        </Card>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-body-sm text-subtle">
              No hay actividad registrada con esos filtros.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          {/* Mobile cards */}
          <div className="md:hidden p-3 space-y-2.5">
            {logs.map((log) => (
              <div key={log._id} className="rounded-md border border-line p-3">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
                  <Badge variant={ACTION_VARIANTS[log.action] || 'secondary'}>
                    {ACTION_LABELS[log.action] || log.action}
                  </Badge>
                  <span className="text-caption text-subtle">
                    {formatDateTime(log.createdAt)}
                  </span>
                </div>
                <p className="text-body-sm text-foreground font-medium">
                  {ENTITY_LABELS[log.entityType] || log.entityType} ·{' '}
                  {log.metadata?.title || '—'}
                </p>
                <div className="text-caption text-subtle mt-1 flex items-center justify-between gap-2">
                  <span>Por {log.performedBy?.name || 'Sistema'}</span>
                  {log.changes && (log.changes.before || log.changes.after) && (
                    <button
                      type="button"
                      onClick={() => showDiff(log)}
                      className="inline-flex min-h-10 items-center gap-1 px-1 text-primary hover:underline"
                    >
                      <Code size={12} /> Ver diff
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-body-sm">
              <thead className="bg-surface-muted border-b border-line">
                <tr>
                  {['Cuándo', 'Quién', 'Acción', 'Entidad', 'Detalle', ''].map((h, i) => (
                    <th
                      key={i}
                      className="text-left px-4 py-3 font-semibold text-foreground"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {logs.map((log) => (
                  <tr key={log._id} className="hover:bg-surface-muted dark:hover:bg-neutral-800/60">
                    <td className="px-4 py-3 text-caption text-subtle">
                      {formatDateTime(log.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {log.performedBy?.name || 'Sistema'}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={ACTION_VARIANTS[log.action] || 'secondary'}>
                        {ACTION_LABELS[log.action] || log.action}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {ENTITY_LABELS[log.entityType] || log.entityType}
                    </td>
                    <td className="px-4 py-3 text-foreground">
                      {log.metadata?.title || '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {log.changes && (log.changes.before || log.changes.after) ? (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => showDiff(log)}
                          aria-label="Ver diff"
                        >
                          <Code size={14} />
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Dialog open={diffOpen} onOpenChange={setDiffOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            {activeLog && (
              <div className="flex flex-wrap items-center gap-2 pr-8">
                <Badge variant={ACTION_VARIANTS[activeLog.action] || 'secondary'}>
                  {ACTION_LABELS[activeLog.action] || activeLog.action}
                </Badge>
                <span className="text-body-sm text-subtle">
                  {ENTITY_LABELS[activeLog.entityType] || activeLog.entityType}
                </span>
                <DialogTitle className="text-h3">
                  · {activeLog.metadata?.title || activeLog.entityId}
                </DialogTitle>
              </div>
            )}
          </DialogHeader>

          {activeLog && (
            <div className="space-y-3">
              <div>
                <p className="text-micro uppercase tracking-wide text-subtle mb-1">
                  Antes
                </p>
                <pre className="rounded-md bg-surface-muted p-3 text-caption font-mono text-foreground whitespace-pre-wrap break-words max-h-72 overflow-auto">
                  {activeLog.changes?.before
                    ? JSON.stringify(activeLog.changes.before, null, 2)
                    : '— sin estado previo —'}
                </pre>
              </div>
              <div>
                <p className="text-micro uppercase tracking-wide text-subtle mb-1">
                  Después
                </p>
                <pre className="rounded-md bg-success-50 dark:bg-success-500/15 p-3 text-caption font-mono text-foreground whitespace-pre-wrap break-words max-h-72 overflow-auto">
                  {activeLog.changes?.after
                    ? JSON.stringify(activeLog.changes.after, null, 2)
                    : '— sin estado posterior —'}
                </pre>
              </div>
              <p className="text-caption text-subtle">
                Realizado por <strong>{activeLog.performedBy?.name || 'Sistema'}</strong>{' '}
                el {new Date(activeLog.createdAt).toLocaleString('es')}
              </p>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDiffOpen(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
